/**
 * Pure selection logic for the body-composition trend chart: turns the synced
 * measurement rows into a chronological series for a chosen metric + time
 * window, and reports the current/previous value (for the headline delta).
 * UI-free so it runs under the app's `test:unit` project.
 */
import { BmiCalculator } from './bmi-calculator';
import type { BodyMeasurementRecord } from '~/powersync/generated/schema';

export type TrendMetric = 'weight' | 'bodyFat' | 'muscle' | 'bmi';
export type TrendPeriod = 'week' | 'month' | 'year';

export interface TrendPoint {
  date: Date;
  value: number;
}

export interface TrendSeries {
  /** Chronological points within the period, nulls dropped. */
  points: TrendPoint[];
  /** Latest value across all measurements (may be outside the period window). */
  current: number | null;
  /** The value immediately before `current` (for the delta). */
  previous: number | null;
}

const PERIOD_DAYS: Record<TrendPeriod, number> = {
  week: 7,
  month: 31,
  year: 366,
};

const DAY_MS = 24 * 60 * 60 * 1000;

export class BodyTrendSeries {
  static build(
    measurements: BodyMeasurementRecord[],
    metric: TrendMetric,
    period: TrendPeriod,
    heightCm: number | null,
    now: Date = new Date(),
  ): TrendSeries {
    // Rows arrive newest-first (listByUser); the chart wants oldest-first.
    const chronological = [...measurements].sort(
      (a, b) =>
        new Date(a.measuredAt ?? 0).getTime() -
        new Date(b.measuredAt ?? 0).getTime(),
    );

    const valued = chronological
      .map((m) => ({
        date: new Date(m.measuredAt ?? 0),
        value: BodyTrendSeries.metricValue(m, metric, heightCm),
      }))
      .filter((p): p is TrendPoint => p.value != null);

    const cutoff = now.getTime() - PERIOD_DAYS[period] * DAY_MS;
    const points = valued.filter((p) => p.date.getTime() >= cutoff);

    return {
      points,
      current: valued.length ? valued[valued.length - 1].value : null,
      previous: valued.length > 1 ? valued[valued.length - 2].value : null,
    };
  }

  private static metricValue(
    measurement: BodyMeasurementRecord,
    metric: TrendMetric,
    heightCm: number | null,
  ): number | null {
    switch (metric) {
      case 'weight':
        return measurement.weight ?? null;
      case 'bodyFat':
        return measurement.bodyFatPercent ?? null;
      case 'muscle':
        return measurement.leanMass ?? null;
      case 'bmi':
        return BmiCalculator.compute(measurement.weight, heightCm);
    }
  }
}
